import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, BarChart3, Building2, CalendarDays, ChevronRight, ClipboardList,
  Database, Eye, FileSpreadsheet, Gauge, LayoutDashboard, LogOut, Menu, Search, Settings2,
  PieChart, Repeat2, Share2, ShieldCheck, Sparkles, Target, UserSquare2, Users, X } from "lucide-react";
import { api, beginAuthTransition, completeAuthTransition } from "./api";
import { PERIODS } from "./lib/format";
import { can } from "./lib/permissions";
import imcLogo from "./assets/imc-logo.png";
import imcLogoDarkBackground from "./assets/imc-logo-dark-bg.png";

const NAV = [
  { label: "Genel Bakış", icon: LayoutDashboard, path: "/", permission: "overview.view" },
  { label: "Hedef Performansı", icon: Target, path: "/targets", permission: "targets.performance.view" },
  { label: "Hedef Giriş", icon: ClipboardList, path: "/target-entry", permission: "targets.entry.view", baseRoles: ["admin","manager"] },
  { label: "Bölgeler", icon: Building2, path: "/regions", permission: "regions.view" },
  { label: "Satıcılar", icon: Users, path: "/sellers", permission: "sellers.view" },
  { label: "Branş Analizi", icon: BarChart3, path: "/branches", permission: "branches.view" },
  { label: "Sigorta Şirketleri", icon: ShieldCheck, path: "/companies", permission: "companies.view" },
  { label: "Müşteriler", icon: UserSquare2, path: "/customers", permission: "customers.view", baseRoles: ["admin","manager","regional_manager"] },
  { label: "Poliçeler", icon: FileSpreadsheet, path: "/policies", permission: "policies.list" },
  { label: "Yeni İş & Yenileme", icon: Repeat2, path: "/business-classification", permission: "business_classification.view" },
  { label: "Yenileme & Vade", icon: CalendarDays, path: "/renewals", permission: "renewals.view" },
  { label: "Portföy Analizi", icon: PieChart, path: "/portfolio-analysis", permission: "portfolio.view" },
  { label: "Çapraz Satış", icon: Share2, path: "/cross-sell", permission: "crosssell.view" },
  { label: "Veri Aktarımı", icon: Database, path: "/import", permission: "imports.history", baseRoles: ["admin", "manager"] },
  { label: "Organizasyon", icon: Users, path: "/organization", permission: "organization.view", baseRoles: ["admin","manager"] },
  { label: "Veri Kalitesi", icon: Gauge, path: "/quality", permission: "quality.view" },
];

export function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const r = await api.post("/auth/login", { email, password });
      beginAuthTransition();
      localStorage.setItem("imc_token", r.data.token);
      completeAuthTransition();
      onLogin(r.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || "Giriş başarısız");
    } finally { setBusy(false); }
  };
  return (
    <main className="login-shell">
      <div className="login-art">
        <div className="art-grid" />
        <div className="login-brand">
          <img data-testid="login-brand-logo" src={imcLogoDarkBackground} alt="IMC Sigorta Reasürans Brokerliği" />
        </div>
        <div className="art-copy">
          <p className="eyebrow">YÖNETİM PLATFORMU · 2026</p>
          <h1>Kararları<br/><em>veriyle</em> hızlandırın.</h1>
          <p>Üretim, hedef ve portföy performansını tek bir yönetim ekranında görün.</p>
        </div>
        <div className="art-footer">
          <span>IMC Sigorta ve Reasürans Brokerliği A.Ş.</span>
          <span>Güvenli çalışma alanı</span>
        </div>
      </div>
      <form className="login-form" onSubmit={submit}>
        <div>
          <p className="eyebrow">Yönetici girişi</p>
          <h2>Hoş geldiniz</h2>
          <p className="muted">IMC Brokerlik çalışma alanınıza giriş yapın.</p>
        </div>
        <label>E-posta
          <input data-testid="login-email-input" name="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required />
        </label>
        <label>Şifre
          <input data-testid="login-password-input" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required />
        </label>
        {error && <div data-testid="login-error" className="error">{error}</div>}
        <button data-testid="login-submit-button" className="primary-button" type="submit" disabled={busy}>
          {busy ? "Kontrol ediliyor..." : "Giriş yap"} <ArrowUpRight size={17} />
        </button>
        <div className="login-note"><ShieldCheck size={16}/> Rol tabanlı erişim · Tüm oturumlar denetim günlüğüne yazılır</div>
      </form>
    </main>
  );
}

export function Layout({ user, dataset, onLogout, onExitImpersonation, children }) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem("imc360.sidebarCollapsed") === "true"; } catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("imc360.sidebarCollapsed", String(collapsed)); } catch {}
  }, [collapsed]);
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);
  const location = useLocation();
  const navigate = useNavigate();
  const nav = NAV.filter((n) => can(user, n.permission) && (!n.baseRoles || n.baseRoles.includes(user.role)));
  const active = nav.find((n) => location.pathname === n.path || location.pathname.startsWith(n.path + "/"));
  const navLabel = (label, path) => user.role === "seller" && path === "/renewals" ? "Benim Yenilemelerim" : label;
  return (
    <div className={"app-shell" + (collapsed ? " sidebar-collapsed" : "")}>
      <button type="button" aria-label="Menüyü kapat" className={"sidebar-backdrop" + (open ? " visible" : "")} onClick={() => setOpen(false)} />
      <aside className={open ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <img className="sidebar-brand-logo" data-testid="sidebar-brand-logo" src={imcLogo} alt="IMC Sigorta Reasürans Brokerliği" />
          <button data-testid="sidebar-close-button" className="mobile-close" onClick={() => setOpen(false)}><X size={18}/></button>
        <button type="button" data-testid="sidebar-toggle-button" className="desktop-menu-toggle" aria-label={collapsed ? "Menüyü genişlet" : "Menüyü daralt"} title={collapsed ? "Menüyü genişlet" : "Menüyü daralt"} aria-expanded={!collapsed} onClick={() => setCollapsed(value => !value)}><Menu size={18}/></button>
        </div>
        <div className={"workspace " + (dataset?.state?.active === "PRODUCTION" ? "prod" : dataset?.state?.active === "DEMO" ? "demo" : "loading")}>
          <span className="status-dot" />
          {dataset?.state?.active === "PRODUCTION" ? "Üretim verisi aktif" :
           dataset?.state?.active === "DEMO" ? "Demo çalışma alanı" : "Veri kaynağı yükleniyor..."}
          <ChevronRight size={14}/>
        </div>
        <nav>
          {nav.map(({ label, icon: Icon, path }) => (
            <button data-testid={`nav-${path.replace("/", "") || "overview"}-button`} key={path} title={navLabel(label, path)}
              className={location.pathname === path || location.pathname.startsWith(path + "/") ? "active" : ""}
              onClick={() => { navigate(path); setOpen(false); }}>
              <Icon size={17}/> <span className="sidebar-nav-label">{navLabel(label, path)}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <button data-testid="settings-button" aria-label="Ayarlar" title="Ayarlar" onClick={() => { navigate("/settings"); setOpen(false); }}><Settings2 size={17}/> <span className="sidebar-nav-label">Ayarlar</span></button>
          <div className="user-chip">
            <div className="avatar">{user.name.split(" ").map((x) => x[0]).join("")}</div>
            <div className="user-chip-info"><strong data-testid="sidebar-user-name">{user.name}</strong><span data-testid="sidebar-user-role">{user.role_label || user.role.replace("_", " ")}</span></div>
            <button data-testid="logout-button" aria-label="Çıkış yap" title="Çıkış yap" className="icon-button" onClick={onLogout}><LogOut size={16}/></button>
          </div>
        </div>
      </aside>
      <div className="main">
        {user.is_impersonating && <div className="impersonation-banner" data-testid="impersonation-banner">
          <div><Eye size={16}/><span><strong data-testid="impersonated-user-name">{user.name}</strong> kullanıcısı olarak görüntülüyorsunuz</span><em>Salt okunur</em></div>
          <button data-testid="exit-impersonation-button" onClick={onExitImpersonation}><LogOut size={14}/>Admin Görünümüne Dön</button>
        </div>}
        <header className="topbar">
          <button data-testid="sidebar-menu-button" className="mobile-menu" onClick={() => setOpen(true)}><Menu size={21}/></button>
          <div className="crumb">
            IMC Brokerlik <ChevronRight size={14}/> <strong>{active ? navLabel(active.label, active.path) : "Ayarlar"}</strong>
          </div>
          <div className="top-actions">
            <span className="live-dot" /> Veriler güncel
            <button data-testid="global-search-button" className="icon-button"><Search size={18}/></button>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}

export function PageHead({ eyebrow, title, desc, actions }) {
  return (
    <div className="page-head">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {desc && <p className="muted">{desc}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function Filters({ period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, isDemo, actions }) {
  return (
    <div className="filters">
      <div className="filter-label"><CalendarDays size={16}/> Rapor dönemi</div>
      {PERIODS.map((p) => (
        <button data-testid={`period-${p.key}-button`} key={p.key}
          className={period === p.key ? "selected" : ""}
          onClick={() => setPeriod(p.key)}>{p.label}</button>
      ))}
      {period === "custom" && (
        <div className="custom-range" data-testid="custom-range-picker">
          <input data-testid="custom-start-date" type="date" value={startDate || ""}
                 onChange={(e) => setStartDate && setStartDate(e.target.value)} />
          <span className="dash">—</span>
          <input data-testid="custom-end-date" type="date" value={endDate || ""}
                 onChange={(e) => setEndDate && setEndDate(e.target.value)} />
        </div>
      )}
      <span className="filter-divider" />
      <select data-testid="year-select" className="year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}
              disabled={period === "custom"}>
        {[2026, 2025, 2024].map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <span className="filter-spacer" />
      {actions}
      {isDemo && <span className="demo-badge" data-testid="demo-badge">DEMO VERİ</span>}
    </div>
  );
}

export function Kpi({ label, value, change, changeType = "positive", sub, testid }) {
  return (
    <div data-testid={testid} className="kpi">
      <div className="kpi-label">{label}<span className="info">i</span></div>
      <strong>{value}</strong>
      {change && <div className={"change " + changeType}>{change}</div>}
      {sub && <span className="kpi-sub">{sub}</span>}
    </div>
  );
}

export function Panel({ title, sub, actions, children, className = "" }) {
  return (
    <div className={"panel " + className}>
      <div className="panel-head">
        <div>
          {title && <h3>{title}</h3>}
          {sub && <span>{sub}</span>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

export function LoadingBlock({ label = "Veriler yükleniyor..." }) {
  return <div data-testid="page-loading" className="empty-state">{label}</div>;
}

export function ErrorBlock({ message }) {
  return <div data-testid="page-error" className="error-block">{message}</div>;
}

export function InsightSpark() {
  return <Sparkles size={18} className="insight-spark" />;
}

export function usePeriod() {
  const [period, setPeriod] = useState("ytd");
  const [year, setYear] = useState(2026);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Build the params object callers can spread directly into api.get({ params: {...periodParams} })
  const periodParams = { period, year };
  if (period === "custom" && startDate && endDate) {
    periodParams.start_date = startDate;
    periodParams.end_date = endDate;
  }
  return { period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams };
}

export function useDataset(user) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!user) { setState(null); return; }
    api.get("/dataset/state").then((r) => setState(r.data)).catch(() => setState(null));
  }, [user?.id]);
  return state;
}
